import { useEffect, useState } from 'react';
import { 
  Star, MessageSquare, ThumbsUp, Flag, CheckCircle, 
  XCircle, MoreHorizontal, Filter, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/lib/api';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  flagged: { label: 'Flagged', color: 'bg-orange-100 text-orange-800' }
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating 
              ? 'fill-yellow-400 text-yellow-400' 
              : 'text-stone-300'
          }`}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReview, setSelectedReview] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [statusFilter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      const res = await api.get(`/enterprise/reviews?${params}`);
      setReviews(res.data.reviews);
    } catch (error) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const moderateReview = async (reviewId, status, reason = null) => {
    try {
      const payload = { status };
      if (reason) payload.moderation_reason = reason;
      
      await api.put(`/enterprise/reviews/${reviewId}`, payload);
      toast.success(`Review ${status}`);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to moderate review');
    }
  };

  const submitResponse = async () => {
    if (!selectedReview || !adminResponse.trim()) return;
    
    try {
      await api.put(`/enterprise/reviews/${selectedReview.review_id}`, {
        admin_response: adminResponse
      });
      toast.success('Response added');
      setSelectedReview(null);
      setAdminResponse('');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to add response');
    }
  };

  return (
    <div className="min-h-screen pb-16" data-testid="reviews-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium text-stone-900">Reviews</h1>
            <p className="text-stone-500 mt-1">Moderate customer reviews</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {['pending', 'approved', 'rejected', 'flagged'].map((status) => {
            const config = statusConfig[status];
            return (
              <div 
                key={status}
                className={`bg-white rounded-lg border p-4 cursor-pointer ${
                  statusFilter === status ? 'ring-2 ring-stone-900' : ''
                }`}
                onClick={() => setStatusFilter(status === statusFilter ? 'all' : status)}
              >
                <p className="text-2xl font-semibold">
                  {reviews.filter(r => r.status === status).length || 0}
                </p>
                <p className="text-sm text-stone-500">{config.label}</p>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reviews</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Review</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <div className="h-16 skeleton rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : reviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <MessageSquare className="h-12 w-12 mx-auto text-stone-300 mb-4" />
                    <p className="text-stone-500">No reviews to moderate</p>
                  </TableCell>
                </TableRow>
              ) : (
                reviews.map((review) => {
                  const config = statusConfig[review.status] || statusConfig.pending;
                  return (
                    <TableRow key={review.review_id}>
                      <TableCell>
                        <p className="font-medium text-sm">{review.product_name}</p>
                      </TableCell>
                      <TableCell>
                        <StarRating rating={review.rating} />
                      </TableCell>
                      <TableCell className="max-w-md">
                        {review.title && (
                          <p className="font-medium text-sm">{review.title}</p>
                        )}
                        <p className="text-sm text-stone-600 line-clamp-2">
                          {review.content}
                        </p>
                        {review.admin_response && (
                          <div className="mt-2 p-2 bg-stone-50 rounded text-xs">
                            <span className="font-medium">Response: </span>
                            {review.admin_response}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-stone-500">
                        {review.customer_id?.substring(0, 12)}...
                        {review.is_verified_purchase && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            Verified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={config.color}>
                          {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-stone-500 text-sm">
                        {new Date(review.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => moderateReview(review.review_id, 'approved')}>
                              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => moderateReview(review.review_id, 'rejected')}>
                              <XCircle className="h-4 w-4 mr-2 text-red-600" />
                              Reject
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => moderateReview(review.review_id, 'flagged')}>
                              <Flag className="h-4 w-4 mr-2 text-orange-600" />
                              Flag
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedReview(review);
                              setAdminResponse(review.admin_response || '');
                            }}>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Respond
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Response Dialog */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Respond to Review</DialogTitle>
            <DialogDescription>
              Your response will be visible to all customers viewing this product
            </DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="py-4">
              <div className="bg-stone-50 p-4 rounded-lg mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <StarRating rating={selectedReview.rating} />
                  <span className="text-sm text-stone-500">
                    by {selectedReview.customer_id?.substring(0, 12)}...
                  </span>
                </div>
                {selectedReview.title && (
                  <p className="font-medium">{selectedReview.title}</p>
                )}
                <p className="text-sm text-stone-600">{selectedReview.content}</p>
              </div>
              <Textarea
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Write your response..."
                rows={4}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedReview(null)}>
              Cancel
            </Button>
            <Button onClick={submitResponse}>
              Submit Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
